import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useC5N6, ItemState, QueryFilters } from './hooks/useC5N6';
import { c5n6Api } from '../../services/c5n6Api';
import { BatchAcceptanceBridge, pwc5n6f1AcceptanceManifest } from '../../features/acceptance';
import './C5N6Form.css';

// -- Helpers --
const fmtNumber = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const fmtPercent = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toFixed(2) + '%';
};

// -- Component --
const C5N6Form: React.FC = () => {
  const navigate = useNavigate();
  const [scStart, setScStart] = useState('');
  const [scEnd, setScEnd] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
    lastApiResponse,
  } = useC5N6();

  // -- Query --
  const onQuery = useCallback(() => {
    const filters: QueryFilters = { scStart, scEnd };
    handleQuery(filters);
  }, [handleQuery, scStart, scEnd]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // -- Execute --
  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆 S/C？`,
      okText: '確定沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  // -- Select All --
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // -- Table columns --
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            handleToggleItem(record.item.fc110_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: 'S/C 單號',
      dataIndex: ['item', 'fc110_01'],
      key: 'fc110_01',
      width: 120,
    },
    {
      title: '部門',
      dataIndex: ['item', 'fc110_10'],
      key: 'fc110_10',
      width: 100,
    },
    {
      title: '客戶名稱',
      dataIndex: ['item', 'fc110_12'],
      key: 'fc110_12',
      width: 220,
      ellipsis: true,
    },
    {
      title: '預估毛利',
      dataIndex: ['item', 'fc110_99'],
      key: 'fc110_99',
      width: 140,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
    {
      title: '毛利率',
      dataIndex: ['item', 'fc110_100'],
      key: 'fc110_100',
      width: 100,
      align: 'right',
      render: (v: number) => fmtPercent(v),
    },
  ];

  // -- Render --
  return (
    <div className="c5n6-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => { e.preventDefault(); navigate('/erp'); }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => { e.preventDefault(); navigate('/erp'); }}
              >
                銷貨管理
              </a>
              <span>/</span>
              <span className="current">S/C 結案沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>S/C 結案沖銷</h1>
            <span className="badge">C5N6</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>S/C 單號:</label>
          <Input
            value={scStart}
            onChange={(e) => setScStart(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始"
            style={{ width: 130 }}
            allowClear
          />
          <span className="separator">~</span>
          <Input
            value={scEnd}
            onChange={(e) => setScEnd(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束"
            style={{ width: 130 }}
            allowClear
          />

          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWC5N6-ACT-QUERY">
            查詢可沖銷 S/C
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆（已結案待沖銷）
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fc110_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入篩選條件並點擊「查詢可沖銷 S/C」查詢已結案的 S/C
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            danger
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
            data-acceptance-action="PWC5N6-ACT-EXECUTE"
          >
            執行沖銷 ({selectedCount})
          </Button>
          <Button
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwc5n6f1AcceptanceManifest}
          records={items}
          selectedKeys={items.filter((item) => item.selected).map((item) => item.item.fc110_01)}
          keyOf={(item) => item.item.fc110_01}
          queryExact={(key) => c5n6Api.query({ sc_start: key, sc_end: key })}
          lastApiResponse={lastApiResponse}
          loading={loading || executeLoading}
          onAutoFill={() => {
            const first = items[0];
            if (first) handleToggleItem(first.item.fc110_01, true);
          }}
          autoFillDisabled={items.length === 0}
          autoFillReason="自動填表只勾選第一筆可取消結案 S/C，不會自動執行沖銷。"
          screenDraft={{ sc_start: scStart, sc_end: scEnd, selected_items: items.filter((item) => item.selected) }}
        />
      </div>
    </div>
  );
};

export default C5N6Form;
